import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, BodyText, Card, ElementContainer, SideDrawer, TextField, withStyles } from 'vopay';
import type { AccountUserRole, PermissionsMap } from 'vopay';
import { Button, Checkbox, UtilButton } from 'UI';
import useSubAccountsEnabled from '@/hooks/useSubAccountsEnabled';
import { useClientLogin } from '@/providers/ClientLogin.provider';
import { useToast } from '@/providers/Toast.provider';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import useFetchRolePermissionsCatalog from '@/hooks/useApi/useFetchRolePermissionsCatalog';
import useUpdateUserRole from '@/hooks/useApi/useUpdateUserRole';
import useCreateUserRole from '@/hooks/useApi/useCreateUserRole';
import useFetchSubAccounts from '@/hooks/useApi/useFetchSubAccounts';
import vopayApi from '@/services/api.config';
import AccountUserPermissionsForm, {
  AccountUserPermissionsFormRef,
  PermissionFormValues,
} from '../UserPermissions/AccountUserPermissionsForm';
import PermissionsSearchField from '../UserPermissions/PermissionsSearchField';
import { propagateRoleToSubAccounts } from './propagateRoleToSubAccounts';
import { propagateRoleUpdateToSubAccounts } from './propagateRoleUpdateToSubAccounts';
import styles from './EditRoleDrawer.module.scss';

const cn = withStyles(styles);

const EDIT_ROLE_FORM_ID = 'editRolePermissionsForm';

type EditRoleDrawerProps = {
  isOpen: boolean;
  role: AccountUserRole | null;
  onClose: () => void;
  onRoleUpdated?: () => void;
};

const EditRoleDrawer: FC<EditRoleDrawerProps> = ({ isOpen, role, onClose, onRoleUpdated }) => {
  const { t } = useTranslation('translations', { keyPrefix: 'Users.UserRoles.editDrawer' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global.Buttons' });
  const { response: loginResponse } = useClientLogin();
  const loggedInEmail = loginResponse?.LoginUserName;

  const [roleName, setRoleName] = useState('');
  const [isApplyingToAllSubaccounts, setIsApplyingToAllSubaccounts] = useState(false);
  const [hasAtLeastOnePermission, setHasAtLeastOnePermission] = useState(false);
  const [permissionSearchTerm, setPermissionSearchTerm] = useState('');
  const [isAllSelected, setIsAllSelected] = useState(false);

  const { catalog, fetching: fetchingCatalog, fetchCatalog } = useFetchRolePermissionsCatalog();
  const { submitting, updateUserRole } = useUpdateUserRole();
  const { createUserRole } = useCreateUserRole();
  const [, , , fetchSubAccountsAsync] = useFetchSubAccounts({});
  const formRef = useRef<AccountUserPermissionsFormRef | null>(null);
  const toast = useToast();
  const alertDialog = useAlertDialog();

  const hasSubaccounts = useSubAccountsEnabled();

  // Reset state every time the drawer opens or the role changes.
  useEffect(() => {
    if (isOpen && role) {
      setRoleName(role.Name);
      setIsApplyingToAllSubaccounts(role.ApplyToAllSubaccounts);
      // hasAtLeastOnePermission is set by the form's onFormStateChange — initial
      // state is true because the role already has permissions assigned.
      setHasAtLeastOnePermission(role.Permissions.length > 0);
      setPermissionSearchTerm('');
    }
  }, [isOpen, role]);

  // Fetch the permissions catalog once when the drawer opens.
  const catalogLength = Object.keys(catalog).length;
  useEffect(() => {
    if (isOpen && loggedInEmail && catalogLength === 0) {
      fetchCatalog(loggedInEmail).catch(() => {
        // Errors are already logged in the hook.
      });
    }
  }, [isOpen, loggedInEmail, catalogLength]);

  // Build a PermissionsMap that pre-checks this role's existing permissions.
  const prefilledCatalog: PermissionsMap = (() => {
    if (!role || Object.keys(catalog).length === 0) return {};
    const owned = new Set(role.Permissions);
    const merged: PermissionsMap = {};
    Object.entries(catalog).forEach(([groupName, group]) => {
      merged[groupName] = {};
      Object.entries(group).forEach(([permKey, item]) => {
        merged[groupName][permKey] = { label: item.label, permitted: owned.has(permKey) };
      });
    });
    return merged;
  })();

  const canSubmit = roleName.trim().length > 0 && hasAtLeastOnePermission;

  const handleSave = async (values: PermissionFormValues) => {
    if (!role) return;
    const selectedPermissionKeys = Object.entries(values)
      .filter(([, v]) => v)
      .map(([k]) => k);

    const trimmedName = roleName.trim();
    const sharedPayload = {
      Name: trimmedName,
      Permissions: selectedPermissionKeys.join(','),
      ApplyToAllSubaccounts: isApplyingToAllSubaccounts,
    };

    const wasApplyingToAll = role.ApplyToAllSubaccounts;
    const nameChanged = trimmedName !== role.Name;
    const originalPermSet = new Set(role.Permissions);
    const permsChanged =
      selectedPermissionKeys.length !== originalPermSet.size ||
      selectedPermissionKeys.some((p) => !originalPermSet.has(p));
    const isOffToOn = isApplyingToAllSubaccounts && hasSubaccounts && !wasApplyingToAll;
    const isOnToOff = !isApplyingToAllSubaccounts && hasSubaccounts && wasApplyingToAll;
    const isOffToOffWithSubaccounts =
      !isApplyingToAllSubaccounts && !wasApplyingToAll && hasSubaccounts;
    const isRenameWhileApplyingToAll =
      isApplyingToAllSubaccounts && hasSubaccounts && wasApplyingToAll && nameChanged;
    const isPermsOnlyUpdateWhileApplyingToAll =
      isApplyingToAllSubaccounts &&
      hasSubaccounts &&
      wasApplyingToAll &&
      !nameChanged &&
      permsChanged;

    // Flag stayed off but the account has sub-accounts: warn the user that
    // the edit only touches this parent role and won't cascade. Two variants
    // based on what they changed (name vs permissions-only). If nothing
    // actually changed, skip the dialog and let the save proceed silently.
    if (isOffToOffWithSubaccounts && (nameChanged || permsChanged)) {
      const confirmation = await alertDialog.caution(
        nameChanged
          ? t('confirmEditNameTitle', {
              defaultValue: "Do you want to update this role's name?",
            })
          : t('confirmEditPermsTitle', {
              defaultValue: "Do you want to update this role's permissions?",
            }),
        <div data-apply-confirm-dialog="off-off-edit">
          {nameChanged ? (
            <div className={styles.confirmDialogCard}>
              <div className={styles.row}>
                <span className={styles.label}>
                  {t('confirmOldName', { defaultValue: 'Old Name' })}
                </span>
                <span className={styles.value}>{role.Name}</span>
              </div>
              <div className={`${styles.row} ${styles.rowDivider}`}>
                <span className={styles.label}>
                  {t('confirmNewName', { defaultValue: 'New Name' })}
                </span>
                <span className={styles.value}>{trimmedName}</span>
              </div>
            </div>
          ) : (
            <div className={styles.confirmDialogCard}>
              <div className={`${styles.row} ${styles.rowCentered}`}>
                <span className={styles.label}>
                  {t('confirmRoleLabel', { defaultValue: 'Role:' })}
                </span>
                <span className={styles.value}>{trimmedName}</span>
              </div>
            </div>
          )}
          <p className={styles.confirmDialogLead}>
            {t('confirmEditLocalOnly', {
              defaultValue: 'Saving these changes will update the role on this account only.',
            })}
          </p>
          <p className={styles.confirmDialogLead}>
            {t('confirmEditNoSubaccounts', {
              defaultValue: "Sub-accounts aren't affected.",
            })}
          </p>
        </div>,
        {
          abortLabel: tGlobal('cancel'),
          confirmLabel: tGlobal('confirm'),
        },
      );

      if (confirmation !== 'confirm') return;
    }

    // On → Off: confirm before detaching the sub-account copies. The copies
    // stay in place (now standalone) but the parent stops cascading edits.
    if (isOnToOff) {
      const subAccountsRes = await fetchSubAccountsAsync({
        ShowToken: true,
        ActiveOnly: true,
      });
      const subAccountsCount = subAccountsRes.Success
        ? subAccountsRes.Subaccounts?.length ?? 0
        : 0;

      const confirmation = await alertDialog.caution(
        t('confirmStopTitle', {
          defaultValue: 'Do you want to stop applying this role to sub-accounts?',
        }),
        <div data-apply-confirm-dialog="on-to-off">
          {nameChanged ? (
            <>
              <div className={styles.confirmDialogCard}>
                <div className={styles.row}>
                  <span className={styles.label}>
                    {t('confirmOldName', { defaultValue: 'Old Name' })}
                  </span>
                  <span className={styles.value}>{role.Name}</span>
                </div>
                <div className={`${styles.row} ${styles.rowDivider}`}>
                  <span className={styles.label}>
                    {t('confirmNewName', { defaultValue: 'New Name' })}
                  </span>
                  <span className={styles.value}>{trimmedName}</span>
                </div>
              </div>
              <p className={styles.confirmDialogHint}>
                {t('confirmStopRenameHint', {
                  defaultValue: 'Role name change applies to only this account.',
                })}
              </p>
            </>
          ) : (
            <div className={styles.confirmDialogCard}>
              <div className={`${styles.row} ${styles.rowCentered}`}>
                <span className={styles.label}>
                  {t('confirmRoleLabel', { defaultValue: 'Role:' })}
                </span>
                <span className={styles.value}>{trimmedName}</span>
              </div>
            </div>
          )}
          <p className={styles.confirmDialogLead}>
            {t('confirmStopLead', { defaultValue: 'Saving this change will:' })}
          </p>
          <ul className={styles.confirmDialogList}>
            <li>
              {t('confirmStopBullet1', {
                defaultValue: `Keep the role applied on ${subAccountsCount} sub-account where it was previously applied as a standalone role no longer linked to this parent.`,
                count: subAccountsCount,
              })}
            </li>
            <li>
              {t('confirmStopBullet2', {
                defaultValue: 'Stop any current or future updates to this role cascading to sub-accounts.',
              })}
            </li>
            <li>
              {t('confirmStopBullet3', {
                defaultValue: 'Prevent new sub-accounts from inheriting this role.',
              })}
            </li>
            <li>
              {t('confirmStopBullet4', {
                defaultValue: 'Leave all current user assignments on sub-accounts unchanged.',
              })}
            </li>
          </ul>
          <p className={styles.confirmDialogLead}>
            {t('confirmStopFooter', {
              defaultValue: 'Sub-accounts will be able to edit their copy of the role independently from now on.',
            })}
          </p>
        </div>,
        {
          abortLabel: tGlobal('cancel'),
          confirmLabel: tGlobal('confirm'),
        },
      );

      if (confirmation !== 'confirm') return;
    }

    // Edit while the flag is already on: show a confirmation dialog that
    // tells the user the change will fan out to every sub-account. Two
    // variants based on whether the name was changed too.
    if (isRenameWhileApplyingToAll || isPermsOnlyUpdateWhileApplyingToAll) {
      const subAccountsRes = await fetchSubAccountsAsync({
        ShowToken: true,
        ActiveOnly: true,
      });
      const subAccountsCount = subAccountsRes.Success
        ? subAccountsRes.Subaccounts?.length ?? 0
        : 0;

      const confirmation = await alertDialog.caution(
        t('confirmUpdateTitle', {
          defaultValue: 'Do you want to update role on this account and sub-accounts?',
        }),
        nameChanged ? (
          <div data-apply-confirm-dialog="rename-update">
            <div className={styles.confirmDialogCard}>
              <div className={styles.row}>
                <span className={styles.label}>
                  {t('confirmOldName', { defaultValue: 'Old Name' })}
                </span>
                <span className={styles.value}>{role.Name}</span>
              </div>
              <div className={`${styles.row} ${styles.rowDivider}`}>
                <span className={styles.label}>
                  {t('confirmNewName', { defaultValue: 'New Name' })}
                </span>
                <span className={styles.value}>{trimmedName}</span>
              </div>
            </div>
            <p className={styles.confirmDialogLead}>
              {t('confirmAppliedToCount', {
                defaultValue: `This role is applied to ${subAccountsCount} sub-accounts.`,
                count: subAccountsCount,
              })}
            </p>
            <ul className={styles.confirmDialogList}>
              <li>
                {t('confirmUpdateBullet1', {
                  defaultValue: `If there is any existing "${role.Name}" role it will be replaced with the new role name and permissions will be updated (if applicable).`,
                })}
              </li>
              <li>
                {t('confirmUpdateBullet2', {
                  defaultValue: 'Updated role will be applied to all the existing and any future sub-accounts.',
                })}
              </li>
            </ul>
          </div>
        ) : (
          <div data-apply-confirm-dialog="perms-update">
            <div className={styles.confirmDialogCard}>
              <div className={`${styles.row} ${styles.rowCentered}`}>
                <span className={styles.label}>
                  {t('confirmRoleLabel', { defaultValue: 'Role:' })}
                </span>
                <span className={styles.value}>{trimmedName}</span>
              </div>
            </div>
            <p className={styles.confirmDialogLead}>
              {t('confirmAppliedToCount', {
                defaultValue: `This role is applied to ${subAccountsCount} sub-accounts.`,
                count: subAccountsCount,
              })}
              <br />
              {t('confirmEditLeadShared', { defaultValue: 'Saving these changes will:' })}
            </p>
            <ul className={styles.confirmDialogList}>
              <li>
                {t('confirmPermsUpdateBullet1', {
                  defaultValue: `Apply this role to all ${subAccountsCount} sub-accounts and any new sub-account.`,
                  count: subAccountsCount,
                })}
              </li>
              <li>
                {t('confirmPermsUpdateBullet2', {
                  defaultValue: 'If there is any existing role with the same name, it will be updated with the new permissions.',
                })}
              </li>
            </ul>
          </div>
        ),
        {
          abortLabel: tGlobal('cancel'),
          confirmLabel: tGlobal('confirm'),
        },
      );

      if (confirmation !== 'confirm') return;
    }

    // Off → On is destructive (creates roles in every sub-account), so block
    // the save behind a confirmation dialog matching the design.
    if (isOffToOn) {
      const confirmation = await alertDialog.caution(
        t('confirmApplyTitle', {
          defaultValue: 'Do you want to apply this role to all sub-accounts?',
        }),
        nameChanged ? (
          <div data-apply-confirm-dialog="rename">
            <div className={styles.confirmDialogCard}>
              <div className={styles.row}>
                <span className={styles.label}>
                  {t('confirmOldName', { defaultValue: 'Old Name' })}
                </span>
                <span className={styles.value}>{role.Name}</span>
              </div>
              <div className={`${styles.row} ${styles.rowDivider}`}>
                <span className={styles.label}>
                  {t('confirmNewName', { defaultValue: 'New Name' })}
                </span>
                <span className={styles.value}>{trimmedName}</span>
              </div>
            </div>
            <ul className={styles.confirmDialogList}>
              <li>
                {t('confirmRenameBullet1', {
                  defaultValue: `If there is any existing "${role.Name}" role it will be replaced with the new role name and permissions will be updated (if applicable).`,
                })}
              </li>
              <li>
                {t('confirmRenameBullet2', {
                  defaultValue: 'New role will be applied to all the existing and any future sub-accounts.',
                })}
              </li>
            </ul>
          </div>
        ) : (
          <div data-apply-confirm-dialog="keep-name">
            <div className={styles.confirmDialogCard}>
              <div className={`${styles.row} ${styles.rowCentered}`}>
                <span className={styles.label}>
                  {t('confirmRoleLabel', { defaultValue: 'Role:' })}
                </span>
                <span className={styles.value}>{trimmedName}</span>
              </div>
            </div>
            <ul className={styles.confirmDialogList}>
              <li>
                {t('confirmKeepNameBullet1', {
                  defaultValue: 'Apply this role to all existing sub-accounts and any new sub-account.',
                })}
              </li>
              <li>
                {t('confirmKeepNameBullet2', {
                  defaultValue: 'If there is any existing role with the same name, it will be updated with the new permissions.',
                })}
              </li>
            </ul>
          </div>
        ),
        {
          abortLabel: tGlobal('cancel'),
          confirmLabel: tGlobal('confirm'),
        },
      );

      if (confirmation !== 'confirm') return;
    }

    try {
      await updateUserRole({ RoleID: role.RoleID, ...sharedPayload });

      // Backend doesn't propagate edits to sub-accounts — fan out manually.
      // Two flows depending on whether the flag was already on:
      //   - was-on  → update each sub-account's existing copy (match by Name)
      //   - off→on  → create the role fresh in every sub-account
      if (isApplyingToAllSubaccounts && hasSubaccounts && wasApplyingToAll) {
        const result = await propagateRoleUpdateToSubAccounts(
          sharedPayload,
          {
            fetchSubAccountsAsync,
            fetchAccountUserRolesWithBearer: (bearer) =>
              vopayApi.api.fetchAccountUserRoles(undefined, bearer),
            updateUserRole,
          },
          // Look up sub-account copies by the role's original name — they
          // haven't been renamed yet, so the new name wouldn't match.
          { lookupName: role.Name },
        );

        const hasSkipped = result.skipped.length > 0;
        const hasFailed = result.failed.length > 0;

        if (hasSkipped || hasFailed) {
          await alertDialog.success(
            t('updateSummaryTitle', { defaultValue: 'Role Updated' }),
            <div>
              <p>
                {t('updateSummaryBody', {
                  defaultValue: `The role has been updated on ${result.numUpdated} of ${result.numTotal} sub-accounts.`,
                  updated: result.numUpdated,
                  total: result.numTotal,
                })}
              </p>
              {hasSkipped && (
                <>
                  <p>
                    {t('updateSkippedHeading', {
                      defaultValue: 'Role not updated on the following sub-accounts:',
                    })}
                  </p>
                  <ul>
                    {result.skipped.map((name) => (
                      <li key={`skipped-${name}`}>{name}</li>
                    ))}
                  </ul>
                </>
              )}
              {hasFailed && (
                <>
                  <p>
                    {t('updateFailedHeading', {
                      defaultValue: 'Update failed on the following sub-accounts:',
                    })}
                  </p>
                  <ul>
                    {result.failed.map((name) => (
                      <li key={`failed-${name}`}>{name}</li>
                    ))}
                  </ul>
                </>
              )}
            </div>,
          );
        } else {
          toast.success(
            t('subaccountPropagationSuccess', {
              defaultValue: `Role "${trimmedName}" was updated on ${result.numTotal} sub-account(s).`,
            }),
          );
        }
      } else if (isApplyingToAllSubaccounts && hasSubaccounts && !wasApplyingToAll) {
        // OFF → ON transition: sub-accounts don't yet have a copy. Create the
        // role fresh in each one, mirroring the create-flow fan-out.
        const result = await propagateRoleToSubAccounts(sharedPayload, {
          fetchSubAccountsAsync,
          createUserRole,
        });

        if (result.numFailed > 0) {
          toast.neutral(
            t('partialSubaccountCreate', {
              defaultValue: `Role "${trimmedName}" updated. Applied to ${result.numCreated} of ${result.numTotal} sub-accounts (${result.numFailed} failed).`,
            }),
          );
        } else {
          toast.success(
            t('subaccountCreateSuccess', {
              defaultValue: `Role "${trimmedName}" updated and applied to ${result.numTotal} sub-account(s).`,
            }),
          );
        }
      } else {
        toast.success(
          t('successMessage', { defaultValue: `Role "${trimmedName}" was updated successfully.` }),
        );
      }

      onRoleUpdated?.();
      onClose();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      alertDialog.error(t('errorTitle', { defaultValue: 'Could not update role' }), message);
    }
  };

  return (
    <SideDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={t('title', { defaultValue: 'Edit Role' })}
      headerFullWidthContent={
        <div className={cn('headerSearchRow')}>
          <PermissionsSearchField
            name="editRolePermissionSearch"
            value={permissionSearchTerm}
            onChange={setPermissionSearchTerm}
            placeholder={t('searchPlaceholder', { defaultValue: 'Search Permissions' })}
          />
          <UtilButton
            size="medium"
            onClick={() => formRef.current?.toggleAll(!isAllSelected)}
            className={cn('toggleAllButton')}
          >
            {isAllSelected
              ? t('disableAll', { defaultValue: 'Disable All' })
              : t('enableAll', { defaultValue: 'Enable All' })}
          </UtilButton>
        </div>
      }
      footer={
        <Box flx="col" gapSize="12px" className={cn('footer')}>
          {hasSubaccounts && (
            <Checkbox
              name="editApplyToAllSubaccounts"
              checked={isApplyingToAllSubaccounts}
              onChange={setIsApplyingToAllSubaccounts}
              label={
                <span className={cn('applyToSubaccountsLabel')}>
                  {t('applyToAllSubaccounts', {
                    defaultValue: 'Apply this role to all existing and future Sub-Accounts',
                  })}
                </span>
              }
            />
          )}
          <Box gapSize="12px" className={cn('footerButtons')}>
            <Button colorScheme="secondary" onClick={onClose}>
              {tGlobal('cancel')}
            </Button>
            <Button
              colorScheme="primary"
              disabled={!canSubmit || fetchingCatalog || submitting}
              form={EDIT_ROLE_FORM_ID}
              type="submit"
            >
              {tGlobal('confirm')}
            </Button>
          </Box>
        </Box>
      }
    >
      <div className={cn('body')}>
        <Card hoverEffect={false}>
          <ElementContainer
            label={t('roleName', { defaultValue: 'Role name' })}
            required
            htmlFor="editRoleName"
            padding="0 0 6px"
            input={
              <TextField
                name="editRoleName"
                placeholder={t('roleNamePlaceholder', { defaultValue: 'e.g. Admin' })}
                value={roleName}
                onChange={(e) => setRoleName(e.target.value)}
                variant="outlineShadow"
              />
            }
          />
        </Card>

        {!fetchingCatalog && Object.keys(prefilledCatalog).length > 0 && (
          <AccountUserPermissionsForm
            ref={formRef}
            permissions={prefilledCatalog}
            id={EDIT_ROLE_FORM_ID}
            searchTerm={permissionSearchTerm}
            onSubmit={handleSave}
            onFormStateChange={(_hasChanges, isValid) => setHasAtLeastOnePermission(isValid)}
            onAllEnabledChange={setIsAllSelected}
          />
        )}

        {fetchingCatalog && (
          <BodyText weight="md">
            {t('loadingPermissions', { defaultValue: 'Loading permissions…' })}
          </BodyText>
        )}
      </div>
    </SideDrawer>
  );
};

export default EditRoleDrawer;
